import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTaskItemStore } from '../../stores/useTaskItemStore';
import { uuid } from '../../types/types';
import styles from './TaskItem.module.css';
import { Text } from '@radix-ui/themes';
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { DEFAULT_TASK_TITLE } from '../../utilities';
import * as Toggle from '@radix-ui/react-toggle';

type TaskItemProps = {
  id: uuid;
  defaultValue?: string;
};

export const TaskItem = React.memo(
  ({ id, defaultValue = DEFAULT_TASK_TITLE }: TaskItemProps) => {
    const item = useTaskItemStore((s) => s.items)[id];
    const activeItemId = useTaskItemStore((s) => s.activeItemId);
    const updateContent = useTaskItemStore((s) => s.updateContent);
    const setActiveItemId = useTaskItemStore((s) => s.setActiveItemId);
    const toggleComplete = useTaskItemStore((s) => s.toggleComplete);
    const toggleFail = useTaskItemStore((s) => s.toggleFail);
    const clearJustCompleted = useTaskItemStore((s) => s.clearJustCompleted);
    const clearJustFailed = useTaskItemStore((s) => s.clearJustFailed);
    const clearJustUpdated = useTaskItemStore((s) => s.clearJustUpdated);

    const [isEditingTitle, setIsEditingTitle] = useState(false);

    const isSelected = activeItemId === id;
    const completed = item?.completed;
    const failed = item?.failed;

    useEffect(() => {
      if (!isSelected) {
        setIsEditingTitle(false);
      }
    }, [isSelected]);

    const handleTitleSubmit = () => {
      setIsEditingTitle(false);
    };

    const handleContainerClick = (e: React.MouseEvent) => {
      if (isEditingTitle) return;
      clearJustCompleted(id);
      clearJustFailed(id);
      clearJustUpdated(id);
      setActiveItemId(id);
    };

    return (
      <div className={styles.container} onClick={handleContainerClick}>
        <Toggle.Root
          pressed={completed || failed}
          onPressedChange={() => {
            if (!isEditingTitle) {
              if (failed) {
                toggleFail(id);
              } else {
                toggleComplete(id);
              }
            }
          }}
          className={classNames(
            styles.checkboxWrapper,
            isEditingTitle && styles.disabledCheckbox
          )}
          disabled={isEditingTitle}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={classNames(
              styles.checkboxCircle,
              completed && styles.checked,
              failed && styles.failed
            )}
          >
            {completed && <CheckIcon className={styles.checkIcon} />}
            {failed && <Cross2Icon className={styles.checkIcon} />}
          </div>
        </Toggle.Root>
        {isEditingTitle ? (
          <input
            type="text"
            className={styles.titleInput}
            value={item?.title || ''}
            placeholder={defaultValue}
            onChange={(e) => updateContent(id, undefined, e.target.value)}
            onBlur={handleTitleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTitleSubmit();
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <Text
            className={classNames(
              styles.titleText,
              completed && styles.completed,
              failed && styles.failed
            )}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setActiveItemId(id);
              setIsEditingTitle(true);
            }}
          >
            {item?.title || defaultValue}
          </Text>
        )}
      </div>
    );
  }
);
